import * as THREE from 'three';

/* ------------------------------------------------------------- scratch ---- */

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _v4 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _bore = new THREE.Vector3();
const _col = new THREE.Color();
const UP = new THREE.Vector3(0, 1, 0);
const FWD = new THREE.Vector3(0, 0, 1);

const rnd = Math.random;
const rr = (a, b) => a + (b - a) * Math.random();

/* -------------------------------------------------------------- noise ----- */

function hash2i(i, j, period, seed) {
  i = ((i % period) + period) % period;
  j = ((j % period) + period) % period;
  let n = (i * 374761393 + j * 668265263 + seed * 1442695041) | 0;
  n = (n ^ (n >> 13)) * 1274126177;
  n = n ^ (n >> 16);
  return (n >>> 0) / 4294967295;
}

function vnoise(x, y, period, seed) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
  const a = hash2i(xi, yi, period, seed);
  const b = hash2i(xi + 1, yi, period, seed);
  const c = hash2i(xi, yi + 1, period, seed);
  const d = hash2i(xi + 1, yi + 1, period, seed);
  return (a + (b - a) * u) * (1 - v) + (c + (d - c) * u) * v;
}

function fbm(x, y, period, seed, oct = 4) {
  let amp = 0.5, f = 1, s = 0, norm = 0;
  for (let o = 0; o < oct; o++) {
    s += amp * vnoise(x * f, y * f, period * f, seed + o * 37);
    norm += amp; amp *= 0.5; f *= 2;
  }
  return s / norm;
}

/**
 * Tiling multi-octave noise with a curl-ish domain warp. R carries the warped
 * body density, G a mid-frequency band used for erosion, B fine detail.
 * This is what makes the smoke read as volume rather than a blurred blob.
 */
function flowNoiseTexture(size = 128) {
  const data = new Uint8Array(size * size * 4);
  const P = 4;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = (x / size) * P, v = (y / size) * P;
      // Domain warp — two offset fbm fields push the lookup around, which is
      // what turns smooth fbm into curling, rolling filaments.
      const wx = u + 1.9 * (fbm(u + 5.2, v + 1.3, P, 11, 3) - 0.5);
      const wy = v + 1.9 * (fbm(u + 1.7, v + 9.2, P, 23, 3) - 0.5);
      const r = fbm(wx, wy, P, 3, 4);
      const g = fbm(u * 2, v * 2, P * 2, 71, 3);
      const b = fbm(u * 4 + 0.5, v * 4 + 0.5, P * 4, 131, 2);
      const i = (y * size + x) * 4;
      data[i] = r * 255;
      data[i + 1] = g * 255;
      data[i + 2] = b * 255;
      data[i + 3] = 255;
    }
  }
  // fbm of value noise clusters around the mean; stretch each channel to the
  // full range or the smoke never crosses its erosion threshold.
  for (let c = 0; c < 3; c++) {
    let lo = 255, hi = 0;
    for (let i = c; i < data.length; i += 4) { if (data[i] < lo) lo = data[i]; if (data[i] > hi) hi = data[i]; }
    const span = Math.max(1, hi - lo);
    for (let i = c; i < data.length; i += 4) data[i] = ((data[i] - lo) / span) * 255;
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/** 2x2 atlas of asymmetric star/petal muzzle flashes. */
function flashAtlasTexture(cell = 192) {
  const size = cell * 2;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(size, size);
  const d = img.data;
  for (let vy = 0; vy < 2; vy++) {
    for (let vx = 0; vx < 2; vx++) {
      const variant = vy * 2 + vx;
      const petals = 5 + variant * 2;          // 5,7,9,11 spikes
      const phase = variant * 1.7;
      const asym = 0.35 + variant * 0.12;
      for (let y = 0; y < cell; y++) {
        for (let x = 0; x < cell; x++) {
          const dx = (x + 0.5) / cell - 0.5, dy = (y + 0.5) / cell - 0.5;
          const r = Math.hypot(dx, dy) * 2;
          const ang = Math.atan2(dy, dx);
          // Petal lobes, broken up so no two spikes match.
          let lobe = Math.abs(Math.cos(ang * petals * 0.5 + phase)) ** 2.2;
          lobe *= 0.55 + 0.45 * vnoise(Math.cos(ang) * 3 + 8, Math.sin(ang) * 3 + 8, 64, variant * 13);
          // The base reach is the radius of the star's BODY; `lobe` only adds
          // the spikes on top. At 0.30 the body was 15% of the cell, so the card
          // was 200 px wide but only ~60 px of it was ever bright and the flash
          // read as a small dot with a few threads. 0.44 gives the gas ball the
          // reference actually shows, with the spikes still on top of it.
          const reach = 0.44 + lobe * (0.56 + asym * Math.sin(ang * 2 + phase) * 0.4);
          let a = Math.max(0, 1 - r / Math.max(0.06, reach));
          a = a ** 1.30;
          const core = Math.exp(-r * 11.0);    // hot white centre
          const inner = Math.exp(-r * 3.6);
          const inten = core * 1.15 + inner * 0.55 + a * 1.30;
          // Colour is a TEMPERATURE, not a copy of the intensity. Folding the
          // intensity into RGB as well as alpha squared the falloff under
          // additive blending and left the petals invisible past the core.
          const heat = Math.min(1, core * 2.4 + inner * 0.75);
          const rC = 1.0;
          const gC = 0.44 + 0.54 * heat;
          const bC = 0.10 + 0.86 * heat;
          const alpha = Math.min(1, inten);
          const px = ((vy * cell + y) * size + (vx * cell + x)) * 4;
          d[px] = rC * 255; d[px + 1] = gC * 255; d[px + 2] = bC * 255;
          d[px + 3] = alpha * 255;
        }
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Soft round glow used for the flash halo and the explosion flash core. */
function glowTexture(size = 128) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,250,235,1)');
  g.addColorStop(0.18, 'rgba(255,222,160,0.72)');
  g.addColorStop(0.48, 'rgba(255,150,55,0.22)');
  g.addColorStop(1, 'rgba(255,110,30,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * 2x2 atlas of bullet-hole masks.
 *   R = spall / pulverised halo   G = hole depth mask
 *   B = radial cracks             A = coverage
 */
function decalAtlasTexture(cell = 192) {
  const size = cell * 2;
  const data = new Uint8Array(size * size * 4);
  for (let vy = 0; vy < 2; vy++) {
    for (let vx = 0; vx < 2; vx++) {
      const variant = vy * 2 + vx;
      const holeR = [0.26, 0.30, 0.22, 0.34][variant];
      const seed = 17 + variant * 91;
      for (let y = 0; y < cell; y++) {
        for (let x = 0; x < cell; x++) {
          const dx = (x + 0.5) / cell - 0.5, dy = (y + 0.5) / cell - 0.5;
          let r = Math.hypot(dx, dy) * 2;
          const ang = Math.atan2(dy, dx);
          const ca = Math.cos(ang), sa = Math.sin(ang);
          // Perturb the radius by angular noise -> irregular, chipped rim.
          const edge = fbm(ca * 2.5 + 4, sa * 2.5 + 4, 32, seed, 3);
          const rr2 = r * (1 + (edge - 0.5) * 0.55);
          const hole = THREE.MathUtils.smoothstep(holeR - rr2, -0.05, 0.07);
          // Cracks: thin high-contrast filaments radiating outward.
          const cn = fbm(ang * 1.6 * 3 + 20, r * 3.5, 64, seed + 5, 3);
          let crack = THREE.MathUtils.smoothstep(cn, 0.60, 0.78);
          crack *= Math.max(0, 1 - Math.abs(rr2 - holeR * 1.6) / 0.72);
          crack *= 1 - hole;
          // Spall: pale pulverised ring, denser right at the rim.
          const sn = fbm(ca * 5 + 11, sa * 5 + 11, 32, seed + 9, 3);
          let spall = Math.max(0, 1 - Math.abs(rr2 - holeR * 1.3) / 0.62) ** 1.5;
          spall *= 0.35 + sn * 0.9;
          spall *= 1 - hole * 0.85;
          const cov = Math.min(1, Math.max(hole, crack * 1.0, spall * 0.8))
            * THREE.MathUtils.smoothstep(rr2, 1.02, 0.5);
          const i = ((vy * cell + y) * size + (vx * cell + x)) * 4;
          data[i] = Math.min(255, spall * 255);
          data[i + 1] = hole * 255;
          data[i + 2] = Math.min(255, crack * 255);
          data[i + 3] = cov * 255;
        }
      }
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/* ------------------------------------------------------------ geometry ---- */

const QUAD = new THREE.PlaneGeometry(1, 1);

function instancedQuad(count, attrs) {
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = QUAD.index;
  geo.setAttribute('position', QUAD.attributes.position);
  geo.setAttribute('uv', QUAD.attributes.uv);
  geo.instanceCount = count;
  const arrays = {};
  for (const key in attrs) {
    const arr = new Float32Array(count * attrs[key]);
    geo.setAttribute(key, new THREE.InstancedBufferAttribute(arr, attrs[key]));
    arrays[key] = arr;
  }
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
  return { geo, arrays };
}

/* ------------------------------------------------------------- pooling ---- */

class Pool {
  constructor(factory, size) {
    this.items = Array.from({ length: size }, factory);
    this.cursor = 0;
  }
  next() {
    const item = this.items[this.cursor];
    this.cursor = (this.cursor + 1) % this.items.length;
    return item;
  }
}

/* ------------------------------------------------------------- shaders ---- */

const SMOKE_VERT = /* glsl */`
  attribute vec3 aPos;
  attribute vec4 aLife;   // x=prog 0..1, y=opacity, z=rotation, w=seed
  attribute vec2 aSize;   // x=radius, y=aspect
  attribute vec3 aColor;
  varying vec2 vUv;
  varying float vProg, vOpacity, vSeed;
  varying vec3 vColor;
  void main() {
    vUv = uv; vProg = aLife.x; vOpacity = aLife.y; vSeed = aLife.w; vColor = aColor;
    vec4 mv = modelViewMatrix * vec4(aPos, 1.0);
    float c = cos(aLife.z), s = sin(aLife.z);
    vec2 p = position.xy * aSize.x * vec2(1.0, aSize.y);
    mv.xy += vec2(p.x * c - p.y * s, p.x * s + p.y * c);
    gl_Position = projectionMatrix * mv;
  }
`;

const SMOKE_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D uNoise;
  uniform vec3 uSunView, uLit, uShadow;
  uniform float uTime;
  varying vec2 vUv;
  varying float vProg, vOpacity, vSeed;
  varying vec3 vColor;

  void main() {
    vec2 uvc = vUv - 0.5;
    float r = length(uvc) * 2.0;
    if (r > 1.0) discard;
    float sd = vSeed;
    // Flow-warp the lookup so the puff churns instead of just scaling.
    vec2 flow = texture2D(uNoise, vUv * 0.55 + vec2(sd, sd * 1.7) + uTime * 0.012).rg - 0.5;
    vec2 uv2 = vUv + flow * (0.18 + vProg * 0.42);
    float d1 = texture2D(uNoise, uv2 * 1.1 + vec2(sd * 3.1, sd * 1.3)).r;
    float d2 = texture2D(uNoise, uv2 * 2.4 - vec2(sd * 2.2, sd * 0.7)).g;
    float d3 = texture2D(uNoise, uv2 * 5.1 + vec2(sd * 1.1, sd * 4.4)).b;
    float density = d1 * 0.56 + d2 * 0.30 + d3 * 0.14;
    float falloff = smoothstep(1.0, 0.18, r);
    float a = density * falloff * 1.65;
    // Erosion: the alpha threshold climbs with age, eating the puff into wisps.
    float ero = 0.16 + pow(vProg, 1.4) * 0.62;
    a = smoothstep(ero, ero + 0.30, a) * vOpacity;
    if (a <= 0.004) discard;

  #ifdef FIRE
    float t = clamp(density * 1.45 - vProg * 1.15, 0.0, 1.0);
    // Blackbody-ish ramp: sooty red -> orange -> yellow-white heart.
    vec3 col = mix(vec3(0.16, 0.030, 0.006), vec3(0.92, 0.33, 0.055), t);
    col = mix(col, vec3(1.50, 1.00, 0.52), pow(t, 3.0));
    // Soot shell. The outside of a real fireball is unburnt carbon lit from
    // within; without it the ball reads as a uniform glowing balloon, and the
    // additive stack has nothing to stop it summing to a flat white disc.
    float soot = smoothstep(0.62, 0.10, density) * (0.25 + vProg * 0.9);
    col *= 1.0 - soot * 0.80;
    // vColor is a per-puff RADIANCE GAIN here, not a tint: ten of these overlap
    // at the core, so the emitter budgets the sum rather than the shader.
    col *= vColor * (1.0 - vProg * 0.62);
    gl_FragColor = vec4(col * a, a);
  #else
    // Fake a spherical normal so the sun rakes across the puff: cheap volume.
    vec3 n = normalize(vec3(uvc * 2.0, sqrt(max(0.0, 1.0 - r * r)) * 0.85));
    float ndl = dot(n, uSunView);
    vec3 col = mix(uShadow, uLit, smoothstep(-0.75, 0.9, ndl));
    col += uLit * pow(max(0.0, ndl), 5.0) * 0.7;             // sun-side rim
    col *= vColor * mix(0.68, 1.16, density);                // internal shading
    gl_FragColor = vec4(col, a);
  #endif
  }
`;

const SPARK_VERT = /* glsl */`
  attribute vec3 aPos;
  attribute vec3 aVel;
  attribute vec4 aParam;  // x=size, y=prog, z=stretch, w=temp
  uniform float uPx, uPxMax;
  varying vec2 vUv;
  varying float vProg, vTemp;
  void main() {
    vUv = uv; vProg = aParam.y; vTemp = aParam.w;
    vec3 pv = (modelViewMatrix * vec4(aPos, 1.0)).xyz;
    vec3 vv = (modelViewMatrix * vec4(aVel, 0.0)).xyz;
    float sp = length(vv.xy);
    vec2 d2 = sp > 1e-4 ? vv.xy / sp : vec2(0.0, 1.0);
    vec2 n2 = vec2(-d2.y, d2.x);
    float dv = max(0.25, -pv.z);
    float wid = clamp(aParam.x, uPx * dv, uPxMax * dv);
    float len = max(aParam.x * (1.0 + sp * aParam.z), wid);
    pv.xy += d2 * (position.y * len) + n2 * (position.x * wid);
    gl_Position = projectionMatrix * vec4(pv, 1.0);
  }
`;

const SPARK_FRAG = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  varying float vProg, vTemp;
  void main() {
    vec2 p = (vUv - 0.5) * 2.0;
    float across = 1.0 - abs(p.x);
    float along = 1.0 - abs(p.y);
    if (across <= 0.0 || along <= 0.0) discard;
    // Head-biased streak: bright at the leading tip, tapering behind.
    float head = smoothstep(-1.0, 1.0, p.y);
    float a = pow(across, 2.0) * pow(along, 0.7) * mix(0.35, 1.0, head);
    float fade = 1.0 - vProg;
    a *= fade * fade;
    // Cool from white-hot through orange to deep red as it flies.
    float t = clamp(vTemp - vProg * 1.15, 0.0, 1.0);
    vec3 col = mix(vec3(1.0, 0.10, 0.02), vec3(1.0, 0.52, 0.10), smoothstep(0.0, 0.55, t));
    col = mix(col, vec3(1.0, 0.95, 0.80), smoothstep(0.62, 1.0, t));
    float glow = pow(across, 6.0) * pow(along, 3.0);
    gl_FragColor = vec4((col * (1.6 + t * 3.5) + vec3(glow * 2.5)) * a, a);
  }
`;

const TRACER_VERT = /* glsl */`
  attribute vec3 aStart;
  attribute vec3 aEnd;
  attribute vec4 aParam;  // x=width, y=intensity, z=seed, w=coreWidth
  uniform float uPx;      // world units per pixel at unit distance
  uniform float uPxMax;   // hard screen-space width cap
  varying vec2 vUv;
  varying float vInten, vSeed, vNear;
  void main() {
    vUv = uv; vInten = aParam.y; vSeed = aParam.z;
    vec3 a = (modelViewMatrix * vec4(aStart, 1.0)).xyz;
    vec3 b = (modelViewMatrix * vec4(aEnd, 1.0)).xyz;
    float t = position.y + 0.5;
    vec3 p = mix(a, b, t);
    vec3 axis = b - a;
    vec3 side = cross(axis, normalize(-p));
    float sl = length(side);
    side = sl > 1e-5 ? side / sl : vec3(1.0, 0.0, 0.0);
    // A 3 cm round at 60 m is a third of a pixel wide, so there is a screen-space
    // FLOOR. There has to be a CEILING too: the tail of a fresh tracer sits on
    // the muzzle, 30 cm from the eye, where 3 cm of world width covers a third
    // of the frame — that was the white slab hanging over the barrel.
    float d = max(0.25, -p.z);
    float w = clamp(aParam.x, uPx * d, uPxMax * d);
    // Fade out the length of the streak that is inside arm's reach for the same
    // reason: a round leaving the barrel is behind the muzzle blast anyway.
    // Ramp in over the first several metres. Shooter-side, the round leaves the
    // barrel inside the muzzle blast; a tracer that is already full brightness
    // 50 cm from the eye is a blown lozenge sitting next to the flash.
    vNear = smoothstep(1.6, 9.0, length(p));
    p += side * (position.x * 2.0) * w;
    gl_Position = projectionMatrix * vec4(p, 1.0);
  }
`;

const TRACER_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D uNoise;
  uniform float uTime;
  varying vec2 vUv;
  varying float vInten, vSeed, vNear;
  void main() {
    float x = abs(vUv.x - 0.5) * 2.0;
    float t = vUv.y;
    // Heat shimmer: the sheath boils slightly along its length.
    float sh = texture2D(uNoise, vec2(t * 2.0 + uTime * 1.7 + vSeed, vSeed)).r;
    float wob = (sh - 0.5) * 0.35;
    x = clamp(x + wob * (1.0 - t * 0.4), 0.0, 1.0);
    float sheath = pow(max(0.0, 1.0 - x), 2.6);
    float core = pow(max(0.0, 1.0 - x * 3.4), 6.0);
    float lead = smoothstep(0.0, 0.30, t) * smoothstep(1.0, 0.86, t);
    float taper = mix(0.25, 1.0, pow(t, 0.6));   // thin, faint tail
    // Bright head. In the reference (M2 firing at night, Wikimedia) a tracer is
    // a thin salmon streak with a distinctly hotter POINT at its leading end;
    // without it the streak reads as a static scratch rather than something
    // travelling.
    float headGlow = smoothstep(0.74, 1.0, t) * pow(max(0.0, 1.0 - x * 1.7), 3.0);
    float a = (sheath * 0.55 + core + headGlow * 0.85) * lead * taper * vInten * vNear;
    if (a <= 0.003) discard;
    vec3 col = mix(vec3(1.0, 0.24, 0.03), vec3(1.0, 0.62, 0.16), sheath);
    col = mix(col, vec3(1.0, 0.90, 0.62), max(core, headGlow));
    // Peak radiance sits just over the 2.6 bloom threshold: the core flares,
    // the sheath does not, and nothing plateaus into a white lozenge.
    gl_FragColor = vec4(col * a * (0.9 + core * 2.2), a);
  }
`;

const DECAL_VERT = /* glsl */`
  attribute vec3 aPos;
  attribute vec3 aTan;
  attribute vec3 aBit;
  attribute vec3 aNrm;
  attribute vec4 aParam;   // x=size, y=variant, z=opacity, w=depth
  attribute vec3 aColor;
  varying vec2 vUv;
  varying vec3 vTs, vColor, vNrm;
  varying float vVariant, vOpacity, vDepth;
  void main() {
    vUv = uv; vVariant = aParam.y; vOpacity = aParam.z; vDepth = aParam.w;
    vColor = aColor; vNrm = aNrm;
    vec3 wp = aPos + aTan * (position.x * aParam.x) + aBit * (position.y * aParam.x);
    vec3 vd = normalize(cameraPosition - wp);
    // 0.15 let the parallax offset reach a third of the card at grazing angles,
    // which smeared the crater across the whole quad. 0.4 caps the shift.
    vTs = vec3(dot(vd, aTan), dot(vd, aBit), max(0.40, dot(vd, aNrm)));
    gl_Position = projectionMatrix * modelViewMatrix * vec4(wp, 1.0);
  }
`;

const DECAL_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D uMap;
  uniform vec3 uSunDir, uSunColor, uAmbient;
  varying vec2 vUv;
  varying vec3 vTs, vColor, vNrm;
  varying float vVariant, vOpacity, vDepth;
  void main() {
    vec2 cell = vec2(mod(vVariant, 2.0), floor(vVariant * 0.5));
    vec2 uv = (clamp(vUv, 0.001, 0.999) + cell) * 0.5;
    vec4 t = texture2D(uMap, uv);
    // Parallax: push the hole lookup away from the viewer so the crater has
    // depth and the near rim occludes the far wall.
    vec2 par = -vTs.xy / vTs.z * vDepth;
    vec2 off = vUv + par;
    // Clamping the offset lookup replicated the border texel outward, painting
    // a solid black rectangle over the whole card at oblique angles. Outside
    // the cell there is simply no crater to sample.
    vec2 ins = step(vec2(0.0), off) * step(off, vec2(1.0));
    vec2 uvp = (clamp(off, 0.001, 0.999) + cell) * 0.5;
    float deep = texture2D(uMap, uvp).g * ins.x * ins.y;
    float hole = max(t.g, deep);
    float lit = clamp(dot(normalize(vNrm), uSunDir), 0.0, 1.0);
    vec3 light = uAmbient + uSunColor * lit;
    vec3 spall = mix(vColor, vec3(1.0), 0.35) * 1.15 * t.r;
    vec3 base = mix(spall, vec3(0.006, 0.005, 0.005), hole);
    base = mix(base, vColor * 0.10, t.b * (1.0 - hole) * 0.9);
    // Inner shadow along the parallax direction sells the crater lip.
    float shadow = clamp(deep - t.g, 0.0, 1.0);
    base *= 1.0 - shadow * 0.75;
    float a = clamp(max(t.a * 1.15, hole), 0.0, 1.0) * vOpacity;
    if (a <= 0.004) discard;
    gl_FragColor = vec4(base * light, a);
  }
`;

const RING_VERT = /* glsl */`
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;

const RING_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D uNoise;
  uniform vec3 uColor;
  uniform float uProg, uThick, uSeed, uInten;
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv - 0.5) * 2.0;
    float r = length(p);
    float ang = atan(p.y, p.x);
    float n = texture2D(uNoise, vec2(ang * 0.16 + uSeed, uProg * 0.3 + uSeed)).r;
    float edge = 0.92 - uThick * 0.5 + (n - 0.5) * 0.12;
    float band = smoothstep(edge - uThick, edge, r) * smoothstep(1.0, edge + 0.02, r);
    float a = band * (1.0 - uProg) * uInten * (0.55 + n * 0.9);
    if (a <= 0.004) discard;
    gl_FragColor = vec4(uColor * a, a);
  }
`;

const CONE_VERT = /* glsl */`
  varying vec2 vUv;
  varying vec3 vN, vV;
  void main() {
    vUv = uv;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vN = normalize(normalMatrix * normal);
    vV = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }
`;

const CONE_FRAG = /* glsl */`
  precision highp float;
  uniform float uInten, uSeed;
  uniform sampler2D uNoise;
  varying vec2 vUv;
  varying vec3 vN, vV;
  void main() {
    float t = vUv.y;                       // 0 at the muzzle, 1 at the tip
    float n = texture2D(uNoise, vec2(vUv.x * 2.0 + uSeed, t * 1.4 + uSeed * 2.0)).r;
    // Hollow shell. Looking down the bore — which is exactly what a first-person
    // camera does — a solid cone projects as a flat disc pasted over the hand.
    // Weighting by the grazing angle leaves only the gas sheath, so the cone
    // reads as a translucent volume with a bright rim.
    float fres = 1.0 - abs(dot(normalize(vN), normalize(vV)));
    float body = pow(1.0 - t, 1.7) * (0.40 + n * 0.95) * pow(fres, 1.7);
    float a = body * uInten;
    if (a <= 0.003) discard;
    vec3 col = mix(vec3(1.0, 0.30, 0.045), vec3(1.0, 0.86, 0.60), pow(1.0 - t, 2.4));
    gl_FragColor = vec4(col * a, a);
  }
`;

/* -------------------------------------------------------------- system ---- */

const SURFACES = {
  concrete: { dust: 0xa8a29a, spark: 0, chip: 0x8e8880, chips: 5, dustAmt: 1.0, decal: 0x9c968d },
  brick:    { dust: 0xa5776a, spark: 0, chip: 0x8c5a4c, chips: 5, dustAmt: 1.0, decal: 0x8e6155 },
  stone:    { dust: 0x9e9a94, spark: 2, chip: 0x86827c, chips: 5, dustAmt: 0.9, decal: 0x8f8b85 },
  metal:    { dust: 0x8f9296, spark: 20, chip: 0x6f7378, chips: 2, dustAmt: 0.16, decal: 0x5c5f63 },
  dirt:     { dust: 0x8a7550, spark: 0, chip: 0x5d4d33, chips: 7, dustAmt: 1.7, decal: 0x4c3f2b },
  sand:     { dust: 0xc2ac82, spark: 0, chip: 0x9c8a68, chips: 4, dustAmt: 1.9, decal: 0x8d7c5c },
  wood:     { dust: 0x9a7a4e, spark: 0, chip: 0xa07a45, chips: 9, dustAmt: 0.5, decal: 0x40301c },
  glass:    { dust: 0xcfe2ea, spark: 0, chip: 0xbfe0ee, chips: 14, dustAmt: 0.25, decal: 0x9fc4d4 },
  water:    { dust: 0xbcd2d8, spark: 0, chip: 0xa9c6d2, chips: 0, dustAmt: 1.2, decal: 0x000000 },
  flesh:    { dust: 0x6b1010, spark: 0, chip: 0x5a0c0c, chips: 3, dustAmt: 0.6, decal: 0x3a0808 },
};

/**
 * Every transient visual: muzzle flash, tracers, impacts, decals, shells,
 * blood, explosions. All pooled — no allocation during combat.
 */
export class Effects {
  constructor(engine, physics) {
    this.engine = engine;
    this.physics = physics;
    this.scene = engine.scene;
    this.time = 0;

    this.noiseTex = flowNoiseTexture(128);
    this.flashTex = flashAtlasTexture(160);
    this.glowTex = glowTexture(128);
    this.decalTex = decalAtlasTexture(192);
    // Kept for API compatibility with anything reaching in for a sprite map.
    this.smokeTex = this.noiseTex;
    this.sparkTex = this.glowTex;

    this._sunView = new THREE.Vector3(0.4, 0.7, 0.3);
    this._sunWorld = new THREE.Vector3(0.4, 0.7, 0.3);
    this._transients = [];
    this._timers = [];

    this._initSmoke();
    this._initSparks();
    this._initMuzzle();
    this._initTracers();
    this._initDecals();
    this._initDebris();
    this._initRings();
    this._initShells();
    this._initBlastLights();

    this._lastShotTime = -99;
  }

  /* ---- smoke & fire billboards --------------------------------------- */
  _initSmoke() {
    const mkSystem = (count, fire, blending, renderOrder) => {
      const { geo, arrays } = instancedQuad(count, { aPos: 3, aLife: 4, aSize: 2, aColor: 3 });
      const mat = new THREE.ShaderMaterial({
        vertexShader: SMOKE_VERT,
        fragmentShader: SMOKE_FRAG,
        defines: fire ? { FIRE: '' } : {},
        uniforms: {
          uNoise: { value: this.noiseTex },
          uSunView: { value: this._sunView },
          uLit: { value: new THREE.Color(1.05, 0.98, 0.90) },
          uShadow: { value: new THREE.Color(0.20, 0.23, 0.30) },
          uTime: { value: 0 },
        },
        transparent: true,
        depthWrite: false,
        blending,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.frustumCulled = false;
      mesh.renderOrder = renderOrder;
      this.scene.add(mesh);
      const parts = Array.from({ length: count }, () => ({
        alive: false, life: 0, ttl: 1, pos: new THREE.Vector3(), vel: new THREE.Vector3(),
        size0: 0.2, size1: 1, rot: 0, spin: 0, opacity: 0.5, seed: 0, turb: 1,
        col: new THREE.Color(1, 1, 1), drag: 1.8, rise: 0.6,
      }));
      return { mesh, mat, geo, arrays, parts, cursor: 0, live: 0 };
    };
    this.smoke = mkSystem(190, false, THREE.NormalBlending, 3);
    this.fire = mkSystem(110, true, THREE.AdditiveBlending, 4);
  }

  _emitPuff(sys, pos, o) {
    const p = sys.parts[sys.cursor];
    sys.cursor = (sys.cursor + 1) % sys.parts.length;
    p.alive = true;
    p.pos.copy(pos);
    p.vel.set((rnd() - 0.5) * 0.5, 0.35 + rnd() * 0.5, (rnd() - 0.5) * 0.5)
      .multiplyScalar(o.velScale ?? 1);
    if (o.normal) p.vel.addScaledVector(o.normal, o.normalPush ?? 1.1);
    p.ttl = p.life = o.ttl ?? (0.7 + rnd() * 0.6);
    p.size0 = o.size0 ?? 0.14;
    p.size1 = o.size1 ?? 0.85;
    p.opacity = o.opacity ?? 0.42;
    p.rot = rnd() * Math.PI * 2;
    p.spin = (rnd() - 0.5) * 1.9;
    p.seed = rnd();
    p.turb = o.turb ?? 1;
    p.drag = o.drag ?? 1.8;
    p.rise = o.rise ?? 0.6;
    p.col.set(o.color ?? 0x8d8477);
    // On the fire layer the colour is a radiance gain, so the emitter can budget
    // what an overlapping stack of puffs sums to.
    if (o.gain != null) p.col.multiplyScalar(o.gain);
    return p;
  }

  /** Smoke / dust puff. `opts.fire` routes it to the additive fire layer. */
  puff(pos, opts = {}) {
    return this._emitPuff(opts.fire ? this.fire : this.smoke, pos, opts);
  }

  _updateSmokeSystem(sys, dt) {
    const { arrays, parts } = sys;
    let live = 0;
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      const o = i * 4, o3 = i * 3, o2 = i * 2;
      if (!p.alive) { arrays.aSize[o2] = 0; continue; }
      p.life -= dt;
      if (p.life <= 0) { p.alive = false; arrays.aSize[o2] = 0; continue; }
      live++;
      const k = 1 - p.life / p.ttl;
      // Curl-ish turbulence: cheap divergence-free-looking swirl field.
      const t = this.time * 0.9;
      const px = p.pos.x, py = p.pos.y, pz = p.pos.z;
      const tx = Math.sin(py * 1.7 + t + p.seed * 6.2) * Math.cos(pz * 1.3 - t * 0.7);
      const ty = Math.sin(pz * 1.5 - t * 0.8) * Math.cos(px * 1.9 + p.seed * 3.1);
      const tz = Math.sin(px * 1.6 + t * 0.6) * Math.cos(py * 1.4 + p.seed * 4.7);
      const ta = p.turb * (0.35 + k * 1.5) * dt;
      p.vel.x += tx * ta; p.vel.y += ty * ta * 0.5 + p.rise * dt; p.vel.z += tz * ta;
      p.vel.multiplyScalar(Math.exp(-p.drag * dt));
      p.pos.addScaledVector(p.vel, dt);

      const size = THREE.MathUtils.lerp(p.size0, p.size1, k ** 0.62);
      // Snap in over the first few percent of life, then taper out. A sine
      // arch reads as a puff that fades *in* — smoke is densest at birth.
      const fade = Math.min(1, k * 14) * (1 - k) ** 0.85;
      arrays.aPos[o3] = p.pos.x; arrays.aPos[o3 + 1] = p.pos.y; arrays.aPos[o3 + 2] = p.pos.z;
      arrays.aLife[o] = k;
      arrays.aLife[o + 1] = p.opacity * fade;
      arrays.aLife[o + 2] = p.rot + p.spin * k;
      arrays.aLife[o + 3] = p.seed;
      arrays.aSize[o2] = size;
      arrays.aSize[o2 + 1] = 1;
      arrays.aColor[o3] = p.col.r; arrays.aColor[o3 + 1] = p.col.g; arrays.aColor[o3 + 2] = p.col.b;
    }
    sys.live = live;
    sys.geo.attributes.aPos.needsUpdate = true;
    sys.geo.attributes.aLife.needsUpdate = true;
    sys.geo.attributes.aSize.needsUpdate = true;
    sys.geo.attributes.aColor.needsUpdate = true;
    sys.mat.uniforms.uTime.value = this.time;
  }

  /* ---- sparks (velocity-stretched) ------------------------------------ */
  _initSparks() {
    const COUNT = 560;
    const { geo, arrays } = instancedQuad(COUNT, { aPos: 3, aVel: 3, aParam: 4 });
    const mat = new THREE.ShaderMaterial({
      vertexShader: SPARK_VERT, fragmentShader: SPARK_FRAG,
      uniforms: { uPx: { value: 0.003 }, uPxMax: { value: 0.02 } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 5;
    this.scene.add(mesh);
    this.sparkSys = {
      mesh, mat, geo, arrays, cursor: 0,
      parts: Array.from({ length: COUNT }, () => ({
        alive: false, life: 0, ttl: 1, size: 0.02, stretch: 0.02, temp: 1,
        pos: new THREE.Vector3(), vel: new THREE.Vector3(),
        grav: 20, drag: 2.4, bounce: 0, planeP: new THREE.Vector3(), planeN: new THREE.Vector3(0, 1, 0),
        trail: 0,
      })),
    };
  }

  /**
   * Hot particles. `opts` accepts { life, size, stretch, temp, gravity, drag,
   * bounce, color } — bounce > 0 makes them ricochet off the impact plane,
   * which is what sells sparks on metal.
   */
  spark(pos, normal, count = 8, spread = 0.9, speedScale = 1, opts = {}) {
    const sys = this.sparkSys;
    const life = opts.life ?? 0.42;
    for (let i = 0; i < count; i++) {
      const s = sys.parts[sys.cursor];
      sys.cursor = (sys.cursor + 1) % sys.parts.length;
      s.alive = true;
      s.pos.copy(pos);
      _v.copy(normal)
        .addScaledVector(_v2.set(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5), spread * 2)
        .normalize();
      s.vel.copy(_v).multiplyScalar((3 + rnd() * 11) * speedScale);
      s.ttl = s.life = life * (0.5 + rnd());
      s.size = (opts.size ?? 0.016) * (0.6 + rnd() * 0.9);
      s.stretch = opts.stretch ?? 0.014;
      s.temp = (opts.temp ?? 1) * (0.75 + rnd() * 0.35);
      s.grav = opts.gravity ?? 20;
      s.drag = opts.drag ?? 2.2;
      s.bounce = opts.bounce ?? 0;
      s.trail = opts.trail ?? 0;
      if (s.bounce) { s.planeP.copy(pos); s.planeN.copy(normal); }
    }
  }

  _updateSparks(dt) {
    const { parts, arrays, geo } = this.sparkSys;
    for (let i = 0; i < parts.length; i++) {
      const s = parts[i];
      const o4 = i * 4, o3 = i * 3;
      if (!s.alive) { arrays.aParam[o4] = 0; continue; }
      s.life -= dt;
      if (s.life <= 0) { s.alive = false; arrays.aParam[o4] = 0; continue; }
      s.vel.y -= s.grav * dt;
      s.vel.multiplyScalar(Math.exp(-s.drag * dt));
      s.pos.addScaledVector(s.vel, dt);
      if (s.bounce) {
        const d = _v.copy(s.pos).sub(s.planeP).dot(s.planeN);
        if (d < 0) {
          s.pos.addScaledVector(s.planeN, -d);
          const vn = s.vel.dot(s.planeN);
          if (vn < 0) s.vel.addScaledVector(s.planeN, -vn * (1 + s.bounce)).multiplyScalar(0.72);
        }
      }
      if (s.trail > 0 && rnd() < s.trail * dt * 4) {
        this.puff(s.pos, {
          color: 0x3a3530, size0: 0.03, size1: 0.14, ttl: 0.35, opacity: 0.16,
          velScale: 0.2, drag: 3, rise: 0.3,
        });
      }
      const k = 1 - s.life / s.ttl;
      arrays.aPos[o3] = s.pos.x; arrays.aPos[o3 + 1] = s.pos.y; arrays.aPos[o3 + 2] = s.pos.z;
      arrays.aVel[o3] = s.vel.x; arrays.aVel[o3 + 1] = s.vel.y; arrays.aVel[o3 + 2] = s.vel.z;
      arrays.aParam[o4] = s.size;
      arrays.aParam[o4 + 1] = k;
      arrays.aParam[o4 + 2] = s.stretch;
      arrays.aParam[o4 + 3] = s.temp;
    }
    geo.attributes.aPos.needsUpdate = true;
    geo.attributes.aVel.needsUpdate = true;
    geo.attributes.aParam.needsUpdate = true;
  }

  /* ---- muzzle flash --------------------------------------------------- */
  _initMuzzle() {
    const vm = this.engine.viewmodelScene;
    this.flashGroup = new THREE.Group();
    this.flashGroup.visible = false;
    vm.add(this.flashGroup);

    // toneMapped: TRUE deliberately. The viewmodel pass writes straight to the
    // sRGB backbuffer with no bloom, so an untone-mapped additive card clips to
    // a flat white slab the instant its sum crosses 1.0 — which is exactly how
    // this blew out. Running the cards through ACES at the scene exposure gives
    // the flash the same highlight roll-off as the gun it is lighting: the core
    // goes white, the arms stay orange, and nothing plateaus.
    const petalMat = () => new THREE.MeshBasicMaterial({
      map: this.flashTex.clone(), blending: THREE.AdditiveBlending, depthWrite: false,
      depthTest: false, transparent: true, toneMapped: true, side: THREE.DoubleSide,
    });

    // Three randomised cross-quads: two rolled about the bore, one facing out.
    this.flashPetals = [];
    for (let i = 0; i < 3; i++) {
      const m = new THREE.Mesh(QUAD, petalMat());
      m.material.map.repeat.set(0.5, 0.5);
      m.renderOrder = 20;
      this.flashGroup.add(m);
      this.flashPetals.push(m);
    }

    // Muzzle-brake port jets.
    this.flashPorts = [];
    for (let i = 0; i < 3; i++) {
      const m = new THREE.Mesh(QUAD, petalMat());
      m.material.map.repeat.set(0.5, 0.5);
      m.renderOrder = 20;
      this.flashGroup.add(m);
      this.flashPorts.push(m);
    }

    // Directional cone of burning propellant gas.
    const coneGeo = new THREE.ConeGeometry(0.5, 1, 14, 1, true);
    coneGeo.translate(0, -0.5, 0);      // apex at origin
    coneGeo.rotateX(-Math.PI / 2);      // grows along -Z
    this.flashCone = new THREE.Mesh(coneGeo, new THREE.ShaderMaterial({
      vertexShader: CONE_VERT, fragmentShader: CONE_FRAG,
      uniforms: { uInten: { value: 1 }, uSeed: { value: 0 }, uNoise: { value: this.noiseTex } },
      transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    }));
    this.flashCone.renderOrder = 19;
    this.flashGroup.add(this.flashCone);

    // Soft halo — the viewmodel pass has no bloom, so bake one in.
    this.flashHalo = new THREE.Mesh(QUAD, new THREE.MeshBasicMaterial({
      map: this.glowTex, blending: THREE.AdditiveBlending, depthWrite: false,
      depthTest: false, transparent: true, toneMapped: true,
    }));
    this.flashHalo.renderOrder = 18;
    this.flashGroup.add(this.flashHalo);

    // Hot core: a small disc carrying the only genuinely clipped radiance in the
    // effect. Everything else stays on the shoulder of the curve, so the flash
    // has one white-hot point instead of a white slab.
    this.flashCore = new THREE.Mesh(QUAD, new THREE.MeshBasicMaterial({
      map: this.glowTex, blending: THREE.AdditiveBlending, depthWrite: false,
      depthTest: false, transparent: true, toneMapped: true,
    }));
    this.flashCore.renderOrder = 21;
    this.flashGroup.add(this.flashCore);

    // Light rig: one in the world for the level, one in viewmodel space so the
    // gun and hands actually get lit by their own flash.
    this.muzzleLight = new THREE.PointLight(0xffb469, 0, 18, 2);
    this.scene.add(this.muzzleLight);
    this.vmLight = new THREE.PointLight(0xffc07a, 0, 2.5, 2);
    this.engine.viewmodelScene.add(this.vmLight);

    // Extra world lights so enemy muzzle flashes light the level too.
    this.extraLights = new Pool(() => {
      const l = new THREE.PointLight(0xffb469, 0, 20, 2);
      this.scene.add(l);
      return { light: l, timer: 0 };
    }, 4);

    // WORLD-SPACE muzzle glow. This is the piece the effect was missing.
    // Everything above lives in `viewmodelScene`, which is rendered AFTER the
    // post stack — so the star never touched bloom and never had any presence
    // in the scene at all. Reference (night M240 live fire, Wikimedia) says the
    // readable part of a muzzle flash is not the star, it is the broad halo
    // around it and the light it throws; that has to be in the world pass.
    // Doubles as the flash for AI weapons, which previously emitted a point
    // light and nothing visible whatsoever.
    this.worldFlashes = new Pool(() => {
      const m = new THREE.Mesh(QUAD, new THREE.MeshBasicMaterial({
        map: this.glowTex, blending: THREE.AdditiveBlending, depthWrite: false,
        transparent: true, toneMapped: false, side: THREE.DoubleSide,
      }));
      m.visible = false;
      m.renderOrder = 7;
      m.frustumCulled = false;
      this.scene.add(m);
      return { mesh: m, t: 0, ttl: 1, peak: 1, size: 0.5 };
    }, 8);

    this.flashTimer = 0;
    this.flashDur = 0.05;
    this.flashScale = 1;
    this.smokeAccum = 0;
  }

  /**
   * Per-map exposure compensation. `Atmosphere` owns `toneMappingExposure` and
   * it ranges 0.50 (Dust II / Al-Rasheed daylight) to 1.15 (Assault, overcast
   * night). A flash authored at one exposure is twice as bright at the other,
   * so every radiance the flash emits is divided through by it. Clamped, not
   * exact, because a night flash SHOULD still feel more dominant than a
   * daylight one — the dark surroundings do that part for free.
   */
  _expo() {
    const e = this.engine.renderer.toneMappingExposure || 0.62;
    return THREE.MathUtils.clamp(0.62 / e, 0.55, 1.25);
  }

  /**
   * World-space muzzle glow + the light it throws. Separate from the viewmodel
   * star so AI weapons can use it on its own.
   */
  worldFlash(pos, scale = 1, fresh = false, slot = null) {
    // The player's own flash always reuses one slot. Cycling the pool let a
    // held trigger stack five live glows on top of each other and the additive
    // sum washed the whole street orange.
    const f = slot || this.worldFlashes.next();
    const e = this._expo();
    f.mesh.visible = true;
    f.mesh.position.copy(pos);
    f.mesh.quaternion.copy(this.engine.camera.quaternion);
    f.t = 0;
    f.ttl = fresh ? 0.080 : 0.058;
    // Bloom threshold is 2.6 linear. The centre of the glow gradient sits well
    // past it so the flash gets a real bloom kernel; the 0.22-alpha shoulder of
    // the same gradient lands near 1.5 and stays out of it. One card gives both
    // the white-hot crown and the orange halo, which is what the reference is.
    f.peak = (fresh ? 8.4 : 6.2) * scale * e;
    f.size = (fresh ? 0.58 : 0.46) * scale;
    f.mesh.scale.setScalar(f.size * 0.72);
    f.mesh.material.color.setRGB(f.peak, f.peak * 0.74, f.peak * 0.42);
    return f;
  }

  _updateWorldFlashes(dt) {
    const cq = this.engine.camera.quaternion;
    for (const f of this.worldFlashes.items) {
      if (!f.mesh.visible) continue;
      f.t += dt;
      const k = f.t / f.ttl;
      if (k >= 1) { f.mesh.visible = false; continue; }
      // Fast decay, slight expansion: the gas ball blows outward as it cools.
      const l = f.peak * (1 - k) ** 2.2;
      f.mesh.material.color.setRGB(l, l * (0.74 - k * 0.18), l * (0.42 - k * 0.22));
      f.mesh.scale.setScalar(f.size * (0.72 + k * 0.6));
      f.mesh.quaternion.copy(cq);
    }
  }

  muzzleFlash(localPos, worldPos, scale = 1) {
    const now = this.time;
    const burstFresh = now - this._lastShotTime > 0.22;   // first round out
    this._lastShotTime = now;

    const g = this.flashGroup;
    g.visible = true;
    g.position.copy(localPos);
    g.quaternion.identity();
    g.scale.setScalar(1);
    // Bore axis is -Z in viewmodel space; jitter it a touch so no two shots
    // point identically.
    g.rotation.set((rnd() - 0.5) * 0.07, (rnd() - 0.5) * 0.07, 0);

    const big = burstFresh ? 1.34 : 0.86 + rnd() * 0.28;
    this.flashScale = scale * big;
    // 36 ms is two frames at 60 Hz and the eye barely registers it. Real flashes
    // are that short, but a game flash has to survive both motion blur and the
    // player's attention; 50/78 ms is still under a tenth of a second.
    this.flashDur = burstFresh ? 0.078 : 0.050;
    this.flashTimer = this.flashDur;

    // Radiance, not display value: these run through ACES at the scene exposure.
    // The first round out of a cold barrel burns unburnt powder from the whole
    // charge, so it is bigger, longer and noticeably whiter than the rounds
    // behind it, which fire into an already-hot muzzle.
    // The first round is BIGGER and longer-lived, not hotter. Pushing radiance
    // as well as size just clipped the whole star into a white mass.
    const ex = this._expo();
    const L = (burstFresh ? 4.4 : 3.7) * ex;
    const base = 0.112 * scale * big;

    for (let i = 0; i < this.flashPetals.length; i++) {
      const m = this.flashPetals[i];
      const use = i === 0 || rnd() < (burstFresh ? 0.9 : 0.45);
      m.visible = use;
      if (!use) continue;
      const variant = (rnd() * 4) | 0;
      m.material.map.offset.set((variant & 1) * 0.5, (variant >> 1) * 0.5);
      const l = L * rr(0.8, 1.1);
      m.material.color.setRGB(l, l * (0.94 - rnd() * 0.08), l * (0.86 - rnd() * 0.14));
      m.position.set(0, 0, -base * (0.16 + i * 0.12));
      if (i === 2) {
        // One quad perpendicular to the bore so the flash has depth off-axis.
        m.rotation.set(Math.PI / 2, 0, rnd() * Math.PI);
        m.scale.setScalar(base * rr(1.0, 1.4));
      } else {
        // Squashed rather than square: real flashes are wider than they are
        // tall, and a non-uniform scale breaks the two cards' symmetry.
        // 0.14 m of card at 0.45 m from the eye is already ~18 degrees; the
        // 3.6x version covered half the frame and read as a white-out.
        m.rotation.set(0, 0, rnd() * Math.PI * 2);
        m.scale.set(base * rr(1.7, 2.3), base * rr(1.35, 1.85), 1);
      }
      // Falling weights across the stack: one dominant card carries the star,
      // the others only break its symmetry. Equal weights sum to a white disc.
      m.material.opacity = [rr(0.78, 0.95), rr(0.34, 0.52), rr(0.22, 0.36)][i];
    }

    // Brake ports: two side jets and one top jet, short and wide.
    for (let i = 0; i < this.flashPorts.length; i++) {
      const m = this.flashPorts[i];
      const on = rnd() < (burstFresh ? 0.85 : 0.6);
      m.visible = on;
      if (!on) continue;
      const variant = (rnd() * 4) | 0;
      m.material.map.offset.set((variant & 1) * 0.5, (variant >> 1) * 0.5);
      const ang = [-1.28, 1.28, 0][i];
      const s = base * rr(0.40, 0.70);
      m.position.set(Math.sin(ang) * s * 0.7, i === 2 ? s * 0.55 : 0, -base * 0.02);
      m.rotation.set(0, 0, ang + (rnd() - 0.5) * 0.4);
      m.scale.set(s * 1.7, s * 0.85, 1);
      const l = L * 0.42;
      m.material.color.setRGB(l, l * 0.62, l * 0.32);
      m.material.opacity = rr(0.5, 0.8);
    }

    // Gas cone: narrow and long. It is a hollow shell in the shader, so it only
    // shows as a sheath around the bore line rather than a disc over the hand.
    // Short and narrow. Rendered down its own axis a long cone reads as a
    // second glowing ball floating half a metre in front of the muzzle,
    // disconnected from the star — which is exactly what it was doing.
    const cl = base * rr(1.5, 2.2) * (burstFresh ? 1.25 : 1);
    this.flashCone.scale.set(base * rr(0.30, 0.44), base * rr(0.30, 0.44), cl);
    this.flashCone.rotation.z = rnd() * Math.PI;
    this.flashCone.material.uniforms.uSeed.value = rnd();
    this.flashCone.material.uniforms.uInten.value = burstFresh ? 0.30 : 0.20;

    // The halo. Reference (night M240) is unambiguous: the orange envelope is
    // 3-4x the radius of the hot core and it is most of what the eye reads as
    // "the muzzle flashed". It was previously a 0.20-opacity afterthought.
    // It out-scales the star and is deliberately soft, but its radiance stays
    // low enough that it never plateaus — only the core clips.
    this.flashHalo.scale.setScalar(base * (burstFresh ? 4.1 : 3.1));
    const hl = (burstFresh ? 1.55 : 1.15) * ex;
    this.flashHalo.material.color.setRGB(hl, hl * 0.52, hl * 0.20);
    this.flashHalo.material.opacity = burstFresh ? 0.58 : 0.44;
    this.flashHalo.position.set(0, 0, -base * 0.2);

    // The one clipped element. Small enough that it reads as a filament of
    // white-hot gas at the crown rather than a blown highlight.
    const cl2 = (burstFresh ? 13 : 10) * ex;
    this.flashCore.material.color.setRGB(cl2, cl2 * 0.92, cl2 * 0.80);
    this.flashCore.scale.setScalar(base * rr(0.50, 0.70));
    this.flashCore.position.set(0, 0, -base * 0.10);
    this.flashCore.visible = true;

    // World-space glow: bloom, and something for the level to see.
    if (!this._playerFlashSlot) this._playerFlashSlot = this.worldFlashes.next();
    this.worldFlash(worldPos, scale * (burstFresh ? 1.12 : 1.0), burstFresh,
      this._playerFlashSlot);

    // Unburnt powder thrown down the bore. In the reference these streaks are
    // the second-most obvious feature after the ball itself — dozens of thin
    // forward-raked lines a couple of metres long.
    _bore.set(0, 0, -1).applyQuaternion(this.engine.camera.quaternion);
    this.spark(worldPos, _bore, burstFresh ? 8 : 5, 0.15, 2.6, {
      life: 0.14, size: 0.013, stretch: 0.05, temp: 1.2, gravity: 3.5, drag: 3.4,
    });

    this.muzzleLight.position.copy(worldPos);
    this.muzzleLight.intensity = (burstFresh ? 85 : 55) * scale * ex;
    this.vmLight.position.copy(localPos).add(_v.set(0, 0.03, 0.10));
    this.vmLight.intensity = (burstFresh ? 3.4 : 2.2) * scale * ex;

    // Barrel smoke builds with sustained fire.
    this.smokeAccum += burstFresh ? 0.35 : 1;
    if (this.smokeAccum > 2.5) {
      this.smokeAccum = 0;
      _v.copy(worldPos);
      this.puff(_v, {
        color: 0x6d6a66, size0: 0.05, size1: 0.42, ttl: 0.9 + rnd() * 0.5,
        opacity: 0.16, velScale: 0.35, rise: 0.5, drag: 1.6, turb: 1.4,
      });
    }
  }

  /** World-space muzzle light for AI weapons (no viewmodel involved). */
  muzzleLightAt(worldPos, scale = 1) {
    const ex = this._expo();
    const e = this.extraLights.next();
    e.light.position.copy(worldPos);
    e.light.intensity = 70 * scale * ex;
    e.timer = 0.06;
    // AI fire used to be an invisible point light. Give it the same world glow
    // the player gets, so a soldier shooting from a window actually flickers.
    this.worldFlash(worldPos, scale * 0.85, rnd() < 0.3);
  }

  /* ---- tracers -------------------------------------------------------- */
  _initTracers() {
    const COUNT = 72;
    const { geo, arrays } = instancedQuad(COUNT, { aStart: 3, aEnd: 3, aParam: 4 });
    const mat = new THREE.ShaderMaterial({
      vertexShader: TRACER_VERT, fragmentShader: TRACER_FRAG,
      uniforms: {
        uNoise: { value: this.noiseTex }, uTime: { value: 0 },
        uPx: { value: 0.004 }, uPxMax: { value: 0.02 },
      },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 6;
    this.scene.add(mesh);
    this.tracerSys = {
      mesh, mat, geo, arrays, cursor: 0,
      parts: Array.from({ length: COUNT }, () => ({
        alive: false, life: 0, ttl: 0, speed: 400, dist: 0, lit: false,
        from: new THREE.Vector3(), dir: new THREE.Vector3(), width: 0.02,
        seed: 0, cracked: false, droop: 0,
      })),
    };
    this.tracerRatio = 0.34;   // roughly every third round is a tracer
    this._shotCounter = 0;
    // Kept so external code poking `.tracers.items` still finds something.
    this.tracers = { items: this.tracerSys.parts };
  }

  tracer(from, to, speed = 420) {
    const sys = this.tracerSys;
    const t = sys.parts[sys.cursor];
    sys.cursor = (sys.cursor + 1) % sys.parts.length;
    t.from.copy(from);
    t.dir.copy(to).sub(from);
    t.dist = t.dir.length() || 0.001;
    t.dir.multiplyScalar(1 / t.dist);
    t.speed = speed;
    t.ttl = t.life = t.dist / speed + 0.05;
    t.alive = true;
    this._shotCounter++;
    t.lit = (this._shotCounter % 3) === 0 || rnd() < 0.08;
    t.width = t.lit ? rr(0.028, 0.040) : 0.013;
    t.seed = rnd();
    t.cracked = false;
    t.droop = t.lit ? 3.2 : 3.0;    // m/s^2 of visual drop; reads as ballistics
    return t;
  }

  _updateTracers(dt) {
    this._exTracer = this._expo();
    const sys = this.tracerSys;
    const { parts, arrays, geo } = sys;
    const camPos = this.engine.camera.position;
    for (let i = 0; i < parts.length; i++) {
      const t = parts[i];
      const o3 = i * 3, o4 = i * 4;
      if (!t.alive) { arrays.aParam[o4 + 1] = 0; continue; }
      t.life -= dt;
      if (t.life <= 0) { t.alive = false; arrays.aParam[o4 + 1] = 0; continue; }

      const travelled = (t.ttl - t.life) * t.speed;
      const head = Math.min(t.dist, travelled);
      // Stretch scales with speed — a fast round is a long streak.
      const stretch = t.speed * (t.lit ? 0.032 : 0.018);
      const tail = Math.max(0.4, Math.min(head, stretch));
      const headT = head / t.speed, tailT = (head - tail) / t.speed;

      _v.copy(t.from).addScaledVector(t.dir, head);
      _v.y -= t.droop * headT * headT;
      _v2.copy(t.from).addScaledVector(t.dir, head - tail);
      _v2.y -= t.droop * tailT * tailT;

      arrays.aStart[o3] = _v2.x; arrays.aStart[o3 + 1] = _v2.y; arrays.aStart[o3 + 2] = _v2.z;
      arrays.aEnd[o3] = _v.x; arrays.aEnd[o3 + 1] = _v.y; arrays.aEnd[o3 + 2] = _v.z;
      const fade = Math.min(1, t.life / 0.05) * (head >= t.dist ? Math.min(1, t.life / 0.05) : 1);
      arrays.aParam[o4] = t.width;
      // A lit tracer must cross the bloom threshold (2.6 linear) or it never
      // blooms, and an un-bloomed tracer reads as a thin scratch no matter how
      // wide you make it. At 1.55 it sat *below* the threshold — which is why
      // tracers looked faint. The unlit round stays well under, so only every
      // third shot carries the glow.
      arrays.aParam[o4 + 1] = (t.lit ? 4.6 : 0.55) * fade * this._exTracer;
      arrays.aParam[o4 + 2] = t.seed;
      arrays.aParam[o4 + 3] = 0.3;

      // Supersonic crack: dust and a snap of displaced air when a round whips
      // past the camera.
      if (!t.cracked) {
        _v3.copy(camPos).sub(t.from);
        const along = _v3.dot(t.dir);
        if (along > 0 && along < head && along < t.dist) {
          _v4.copy(t.from).addScaledVector(t.dir, along);
          const d = _v4.distanceTo(camPos);
          if (d < 3.0) {
            t.cracked = true;
            const amt = 1 - d / 3.0;
            for (let k = 0; k < 3; k++) {
              _v3.copy(_v4).add(_v2.set((rnd() - 0.5) * 1.2, (rnd() - 0.5) * 1.2, (rnd() - 0.5) * 1.2));
              this.puff(_v3, {
                color: 0x9c968c, size0: 0.05, size1: 0.55 + amt * 0.6,
                ttl: 0.45 + rnd() * 0.3, opacity: 0.10 + amt * 0.14,
                velScale: 0.4, drag: 2.6, rise: 0.2, turb: 2.2,
              });
            }
          }
        }
      }
    }
    geo.attributes.aStart.needsUpdate = true;
    geo.attributes.aEnd.needsUpdate = true;
    geo.attributes.aParam.needsUpdate = true;
    sys.mat.uniforms.uTime.value = this.time;
  }

  /* ---- decals --------------------------------------------------------- */
  _initDecals() {
    const COUNT = 180;
    const { geo, arrays } = instancedQuad(COUNT, {
      aPos: 3, aTan: 3, aBit: 3, aNrm: 3, aParam: 4, aColor: 3,
    });
    const mat = new THREE.ShaderMaterial({
      vertexShader: DECAL_VERT, fragmentShader: DECAL_FRAG,
      uniforms: {
        uMap: { value: this.decalTex },
        uSunDir: { value: this._sunWorld },
        uSunColor: { value: new THREE.Color(0.85, 0.78, 0.68) },
        uAmbient: { value: new THREE.Color(0.34, 0.36, 0.42) },
      },
      transparent: true, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -8, polygonOffsetUnits: -8,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    this.scene.add(mesh);
    this.decalSys = {
      mesh, mat, geo, arrays, cursor: 0,
      parts: Array.from({ length: COUNT }, () => ({ alive: false, life: 0, ttl: 1, opacity: 1 })),
    };
    this.decals = { items: this.decalSys.parts };
  }

  decal(pos, normal, size = 0.09, surface = 'concrete') {
    const sys = this.decalSys;
    const i = sys.cursor;
    sys.cursor = (sys.cursor + 1) % sys.parts.length;
    const d = sys.parts[i];
    const s = SURFACES[surface] ?? SURFACES.concrete;
    if (surface === 'water') return d;

    d.alive = true;
    d.ttl = d.life = 30;
    d.opacity = surface === 'glass' ? 0.75 : 0.95;

    // Tangent frame on the surface, randomly rolled.
    _v.copy(normal).normalize();
    _v2.set(0, 1, 0);
    if (Math.abs(_v.dot(_v2)) > 0.94) _v2.set(1, 0, 0);
    _v3.crossVectors(_v2, _v).normalize();
    _v4.crossVectors(_v, _v3).normalize();
    const a = rnd() * Math.PI * 2, ca = Math.cos(a), sa = Math.sin(a);
    const tx = _v3.x * ca + _v4.x * sa, ty = _v3.y * ca + _v4.y * sa, tz = _v3.z * ca + _v4.z * sa;
    const bx = -_v3.x * sa + _v4.x * ca, by = -_v3.y * sa + _v4.y * ca, bz = -_v3.z * sa + _v4.z * ca;

    const { arrays } = sys;
    const o3 = i * 3, o4 = i * 4;
    const push = 0.008 + size * 0.02;
    arrays.aPos[o3] = pos.x + _v.x * push;
    arrays.aPos[o3 + 1] = pos.y + _v.y * push;
    arrays.aPos[o3 + 2] = pos.z + _v.z * push;
    arrays.aTan[o3] = tx; arrays.aTan[o3 + 1] = ty; arrays.aTan[o3 + 2] = tz;
    arrays.aBit[o3] = bx; arrays.aBit[o3 + 1] = by; arrays.aBit[o3 + 2] = bz;
    arrays.aNrm[o3] = _v.x; arrays.aNrm[o3 + 1] = _v.y; arrays.aNrm[o3 + 2] = _v.z;
    arrays.aParam[o4] = size * rr(0.8, 1.35);
    arrays.aParam[o4 + 1] = (rnd() * 4) | 0;
    arrays.aParam[o4 + 2] = d.opacity;
    arrays.aParam[o4 + 3] = 0.055 + rnd() * 0.03;
    _col.set(s.decal);
    arrays.aColor[o3] = _col.r; arrays.aColor[o3 + 1] = _col.g; arrays.aColor[o3 + 2] = _col.b;
    sys.geo.attributes.aPos.needsUpdate = true;
    sys.geo.attributes.aTan.needsUpdate = true;
    sys.geo.attributes.aBit.needsUpdate = true;
    sys.geo.attributes.aNrm.needsUpdate = true;
    sys.geo.attributes.aParam.needsUpdate = true;
    sys.geo.attributes.aColor.needsUpdate = true;
    return d;
  }

  _updateDecals(dt) {
    const sys = this.decalSys;
    let dirty = false;
    for (let i = 0; i < sys.parts.length; i++) {
      const d = sys.parts[i];
      if (!d.alive) continue;
      d.life -= dt;
      if (d.life <= 0) {
        d.alive = false;
        sys.arrays.aParam[i * 4] = 0;
        dirty = true;
        continue;
      }
      if (d.life < 2.5) {
        sys.arrays.aParam[i * 4 + 2] = d.opacity * (d.life / 2.5);
        dirty = true;
      }
    }
    if (dirty) sys.geo.attributes.aParam.needsUpdate = true;
  }

  /* ---- debris (chips, splinters, shards) ------------------------------ */
  _initDebris() {
    const COUNT = 160;
    const geo = new THREE.TetrahedronGeometry(0.5, 0);
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0.05, vertexColors: false });
    const mesh = new THREE.InstancedMesh(geo, mat, COUNT);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    this.scene.add(mesh);
    const dummy = new THREE.Object3D();
    this.debrisSys = {
      mesh, cursor: 0, dummy,
      parts: Array.from({ length: COUNT }, () => ({
        alive: false, life: 0, ttl: 1, size: 0.02,
        pos: new THREE.Vector3(), vel: new THREE.Vector3(),
        rot: new THREE.Euler(), spin: new THREE.Vector3(),
        planeP: new THREE.Vector3(), planeN: new THREE.Vector3(0, 1, 0),
        col: new THREE.Color(1, 1, 1), flat: 1,
      })),
    };
    // Park everything off-screen.
    for (let i = 0; i < COUNT; i++) {
      dummy.position.set(0, -9999, 0); dummy.scale.setScalar(0.0001);
      dummy.updateMatrix(); mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, _col.set(0xffffff));
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  chips(pos, normal, count, opts = {}) {
    const sys = this.debrisSys;
    for (let i = 0; i < count; i++) {
      const d = sys.parts[sys.cursor];
      sys.cursor = (sys.cursor + 1) % sys.parts.length;
      d.alive = true;
      d.pos.copy(pos).addScaledVector(normal, 0.02);
      _v.copy(normal)
        .addScaledVector(_v2.set(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5), opts.spread ?? 1.5)
        .normalize();
      d.vel.copy(_v).multiplyScalar((1.6 + rnd() * 4.5) * (opts.speed ?? 1));
      d.vel.y += (opts.lift ?? 1.2) * rnd();
      d.ttl = d.life = (opts.ttl ?? 1.6) * (0.6 + rnd() * 0.8);
      d.size = (opts.size ?? 0.02) * (0.5 + rnd());
      d.flat = opts.flat ?? 1;
      d.rot.set(rnd() * 6.28, rnd() * 6.28, rnd() * 6.28);
      d.spin.set((rnd() - 0.5) * 26, (rnd() - 0.5) * 26, (rnd() - 0.5) * 26);
      d.planeP.copy(pos); d.planeN.copy(normal);
      d.col.set(opts.color ?? 0x8b8680).multiplyScalar(0.22 + rnd() * 0.30);
    }
  }

  _updateDebris(dt) {
    const sys = this.debrisSys;
    const { parts, mesh, dummy } = sys;
    let dirty = false;
    for (let i = 0; i < parts.length; i++) {
      const d = parts[i];
      if (!d.alive) continue;
      d.life -= dt;
      dirty = true;
      if (d.life <= 0) {
        d.alive = false;
        dummy.position.set(0, -9999, 0); dummy.scale.setScalar(0.0001);
        dummy.updateMatrix(); mesh.setMatrixAt(i, dummy.matrix);
        continue;
      }
      d.vel.y -= 22 * dt;
      d.vel.multiplyScalar(Math.exp(-0.8 * dt));
      d.pos.addScaledVector(d.vel, dt);
      const dist = _v.copy(d.pos).sub(d.planeP).dot(d.planeN);
      if (dist < 0) {
        d.pos.addScaledVector(d.planeN, -dist);
        const vn = d.vel.dot(d.planeN);
        if (vn < 0) {
          d.vel.addScaledVector(d.planeN, -vn * 1.35).multiplyScalar(0.55);
          d.spin.multiplyScalar(0.5);
        }
      }
      d.rot.x += d.spin.x * dt; d.rot.y += d.spin.y * dt; d.rot.z += d.spin.z * dt;
      const k = d.life / d.ttl;
      const s = d.size * Math.min(1, k * 3);
      dummy.position.copy(d.pos);
      dummy.rotation.copy(d.rot);
      dummy.scale.set(s, s * d.flat, s);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, d.col);
    }
    if (dirty) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }

  /* ---- rings (spall / shockwave) -------------------------------------- */
  _initRings() {
    this.rings = new Pool(() => {
      const mat = new THREE.ShaderMaterial({
        vertexShader: RING_VERT, fragmentShader: RING_FRAG,
        uniforms: {
          uNoise: { value: this.noiseTex },
          uColor: { value: new THREE.Color(1, 1, 1) },
          uProg: { value: 0 }, uThick: { value: 0.3 }, uSeed: { value: 0 }, uInten: { value: 1 },
        },
        transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        polygonOffset: true, polygonOffsetFactor: -6, polygonOffsetUnits: -6,
      });
      const m = new THREE.Mesh(QUAD, mat);
      m.visible = false;
      m.renderOrder = 4;
      m.frustumCulled = false;
      this.scene.add(m);
      return { mesh: m, life: 0, ttl: 1, r0: 1, r1: 2 };
    }, 12);
  }

  ring(pos, normal, opts = {}) {
    const r = this.rings.next();
    r.mesh.visible = true;
    r.mesh.position.copy(pos).addScaledVector(normal, 0.02);
    _q.setFromUnitVectors(FWD, _v.copy(normal).normalize());
    r.mesh.quaternion.copy(_q);
    r.ttl = r.life = opts.ttl ?? 0.35;
    r.r0 = opts.r0 ?? 0.1;
    r.r1 = opts.r1 ?? 0.6;
    const u = r.mesh.material.uniforms;
    u.uColor.value.set(opts.color ?? 0xffffff);
    u.uThick.value = opts.thickness ?? 0.35;
    u.uInten.value = opts.intensity ?? 1;
    u.uSeed.value = rnd();
    u.uProg.value = 0;
    return r;
  }

  _updateRings(dt) {
    for (const r of this.rings.items) {
      if (!r.mesh.visible) continue;
      r.life -= dt;
      if (r.life <= 0) { r.mesh.visible = false; continue; }
      const k = 1 - r.life / r.ttl;
      r.mesh.material.uniforms.uProg.value = k;
      r.mesh.scale.setScalar(THREE.MathUtils.lerp(r.r0, r.r1, 1 - (1 - k) ** 2) * 2);
    }
  }

  /* ---- shells --------------------------------------------------------- */
  _initShells() {
    // Rimmed case: body + slight taper + a darker rim reads better in motion.
    const geo = new THREE.CylinderGeometry(0.0046, 0.0053, 0.022, 9, 1);
    this.shells = new Pool(() => {
      const mat = new THREE.MeshStandardMaterial({
        color: 0xc08b3e, metalness: 1, roughness: 0.30,
        emissive: new THREE.Color(0xff5a12), emissiveIntensity: 0,
      });
      const m = new THREE.Mesh(geo, mat);
      m.visible = false;
      m.castShadow = true;
      this.scene.add(m);
      return {
        mesh: m, body: null, heat: 0, wisp: 0,
        _body: {
          position: m.position, quaternion: m.quaternion, velocity: new THREE.Vector3(),
          radius: 0.012, drag: 0.6, restitution: 0.36, life: 6,
          spin: { axis: new THREE.Vector3(1, 0, 0), rate: 26 },
          onBounce: null, onExpire: null,
        },
      };
    }, 40);
  }

  ejectShell(worldPos, camQuat, playerVel) {
    const s = this.shells.next();
    const m = s.mesh;
    m.visible = true;
    m.position.copy(worldPos);
    m.quaternion.copy(camQuat);
    m.material.emissiveIntensity = 2.6;
    m.material.color.setRGB(1.0, 0.72, 0.36);
    s.heat = 1;
    s.wisp = 0.32;

    const b = s._body;
    _v.set(1, 0.62, 0.18).applyQuaternion(camQuat).normalize();
    b.velocity.copy(_v).multiplyScalar(2.5 + rnd() * 1.3);
    if (playerVel) b.velocity.addScaledVector(playerVel, 0.9);
    b.life = 6;
    // Tumble mostly end-over-end: spin axis nearly perpendicular to the case.
    _v2.set(rnd() - 0.5, (rnd() - 0.5) * 0.15, rnd() - 0.5).normalize().applyQuaternion(camQuat);
    b.spin.axis.copy(_v2);
    b.spin.rate = 24 + rnd() * 16;
    b.onBounce = (n, speed) => { if (speed > 0.8) this.onShellBounce?.(m.position, speed); };
    b.onExpire = () => { m.visible = false; s.body = null; };
    if (!s.body) s.body = this.physics.addBody(b);
    else if (this.physics.bodies.indexOf(b) < 0) s.body = this.physics.addBody(b);

    // Puff of gas out of the ejection port.
    this.puff(worldPos, {
      color: 0x8a8681, size0: 0.02, size1: 0.16, ttl: 0.5, opacity: 0.13,
      velScale: 0.3, drag: 2.4, rise: 0.5, turb: 1.6,
    });
  }

  _updateShells(dt) {
    for (const s of this.shells.items) {
      if (!s.mesh.visible) continue;
      if (s.heat > 0) {
        s.heat = Math.max(0, s.heat - dt);
        const h = s.heat * s.heat;
        s.mesh.material.emissiveIntensity = h * 3.0;
        s.mesh.material.color.setRGB(
          THREE.MathUtils.lerp(0.75, 1.0, h),
          THREE.MathUtils.lerp(0.55, 0.72, h),
          THREE.MathUtils.lerp(0.24, 0.36, h)
        );
      }
      if (s.wisp > 0) {
        s.wisp -= dt;
        if (rnd() < dt * 7) {
          this.puff(s.mesh.position, {
            color: 0x7d7a76, size0: 0.012, size1: 0.09, ttl: 0.4, opacity: 0.10,
            velScale: 0.15, drag: 2.6, rise: 0.55, turb: 2.0,
          });
        }
      }
      if (s._body.spin) s._body.spin.rate *= Math.exp(-0.9 * dt);
    }
  }

  /* ---- composite events ----------------------------------------------- */

  impact(point, normal, surface = 'concrete') {
    const s = SURFACES[surface] ?? SURFACES.concrete;

    if (surface === 'flesh') {
      for (let i = 0; i < 4; i++) {
        this.puff(point, {
          color: 0x5c0d0d, size0: 0.05, size1: 0.26 + rnd() * 0.15, ttl: 0.5,
          opacity: 0.55, normal, normalPush: 1.4, velScale: 0.7, drag: 3.2, rise: -0.6,
        });
      }
      this.chips(point, normal, 4, {
        color: 0x4a0a0a, size: 0.008, speed: 1.4, ttl: 0.8, flat: 0.5,
      });
      this.ring(point, normal, { color: 0x7a1414, r0: 0.03, r1: 0.12, ttl: 0.14, intensity: 0.22, thickness: 0.5 });
      return;
    }

    this.decal(point, normal, surface === 'glass' ? 0.20 : 0.16, surface);

    switch (surface) {
      case 'metal': {
        // Hot shower that ricochets off the plate and cools to red.
        this.spark(point, normal, 22, 0.75, 1.7, {
          life: 0.55, size: 0.017, stretch: 0.018, temp: 1.15, bounce: 0.45, drag: 1.6,
        });
        this.spark(point, normal, 6, 1.5, 0.7, {
          life: 0.9, size: 0.012, stretch: 0.012, temp: 0.75, bounce: 0.5, gravity: 22,
        });
        this.chips(point, normal, 2, { color: 0x60636a, size: 0.011, speed: 1.4, ttl: 1.0 });
        this.puff(point, {
          color: 0x7d8087, size0: 0.05, size1: 0.24, ttl: 0.4, opacity: 0.16,
          normal, velScale: 0.6, drag: 2.6,
        });
        this.ring(point, normal, { color: 0xffc48a, r0: 0.03, r1: 0.15, ttl: 0.10, intensity: 0.35, thickness: 0.5 });
        break;
      }
      case 'wood': {
        this.chips(point, normal, 9, {
          color: 0xa87d45, size: 0.013, speed: 1.5, ttl: 1.6, flat: 0.22, spread: 1.0,
        });
        this.puff(point, {
          color: 0xa98d5e, size0: 0.05, size1: 0.30, ttl: 0.6, opacity: 0.28,
          normal, velScale: 0.7, drag: 2.4,
        });
        this.spark(point, normal, 3, 0.7, 0.5, { life: 0.25, temp: 0.5, size: 0.010 });
        break;
      }
      case 'glass': {
        this.chips(point, normal, 16, {
          color: 0xbfe4f2, size: 0.011, speed: 1.9, ttl: 2.4, flat: 0.14, spread: 1.2, lift: 0.4,
        });
        this.ring(point, normal, { color: 0xa8dcf0, r0: 0.04, r1: 0.22, ttl: 0.16, intensity: 0.30, thickness: 0.45 });
        this.puff(point, {
          color: 0xd6e8f0, size0: 0.04, size1: 0.22, ttl: 0.5, opacity: 0.15,
          normal, velScale: 0.8, drag: 2.8,
        });
        this.spark(point, normal, 5, 1.0, 0.8, { life: 0.3, temp: 0.9, size: 0.008 });
        break;
      }
      case 'water': {
        for (let i = 0; i < 12; i++) {
          this.chips(point, normal, 1, {
            color: 0xcfe4ec, size: 0.012, speed: 2.4, ttl: 0.7, flat: 1.6, spread: 0.45, lift: 2.2,
          });
        }
        this.puff(point, {
          color: 0xdfeef4, size0: 0.08, size1: 0.55, ttl: 0.7, opacity: 0.32,
          normal, normalPush: 1.6, velScale: 1.1, drag: 2.2, rise: -0.4,
        });
        this.ring(point, normal, { color: 0xbcd8e4, r0: 0.06, r1: 0.42, ttl: 0.30, intensity: 0.22, thickness: 0.3 });
        break;
      }
      case 'dirt':
      case 'sand': {
        this.chips(point, normal, s.chips, {
          color: s.chip, size: 0.010, speed: 1.2, ttl: 1.3, spread: 1.3,
        });
        this.puff(point, {
          color: s.dust, size0: 0.06, size1: 0.38 * s.dustAmt, ttl: 0.34,
          opacity: 0.8, normal, normalPush: 2.6, velScale: 1.2, drag: 4.6, rise: 0.1,
        });
        for (let i = 0; i < 4; i++) {
          this.puff(point, {
            color: s.dust, size0: 0.09, size1: (0.70 + rnd() * 0.50) * s.dustAmt,
            ttl: 0.9 + rnd() * 0.7, opacity: 0.55, normal, normalPush: 1.3,
            velScale: 0.9, drag: 2.0, rise: 0.35, turb: 1.4,
          });
        }
        this.ring(point, normal, { color: 0x9c8a68, r0: 0.05, r1: 0.26, ttl: 0.20, intensity: 0.12, thickness: 0.5 });
        break;
      }
      default: {
        // Concrete / brick / stone: pale dust, chips, and a spall ring.
        if (s.spark) {
          this.spark(point, normal, s.spark, 0.9, 1.0, { life: 0.3, temp: 0.85, size: 0.011 });
        }
        this.chips(point, normal, s.chips, {
          color: s.chip, size: 0.009, speed: 1.5, ttl: 1.4, spread: 1.2,
        });
        // Pulverised cement leaves the hole faster than it can be blown away:
        // one dense, short-lived core puff reads as the strike itself, and the
        // slower cloud behind it is what drifts off.
        this.puff(point, {
          color: s.dust, size0: 0.05, size1: 0.30 * s.dustAmt, ttl: 0.30,
          opacity: 0.75, normal, normalPush: 2.4, velScale: 1.1, drag: 5.0, rise: 0.1,
        });
        for (let i = 0; i < 3; i++) {
          this.puff(point, {
            color: s.dust, size0: 0.07, size1: (0.55 + rnd() * 0.35) * s.dustAmt,
            ttl: 0.8 + rnd() * 0.6, opacity: 0.52, normal, normalPush: 1.35,
            velScale: 0.85, drag: 2.2, rise: 0.3, turb: 1.6,
          });
        }
        this.ring(point, normal, { color: 0xd8d2c6, r0: 0.04, r1: 0.22, ttl: 0.16, intensity: 0.22, thickness: 0.5 });
        break;
      }
    }
  }

  /* ---- explosion ------------------------------------------------------ */

  _schedule(delay, fn) {
    this._timers.push({ t: delay, fn });
  }

  /**
   * Pooled blast lights. Allocating a PointLight per explosion also forced a
   * shader recompile of every lit material in the scene the first time each new
   * light appeared, which is a frame hitch on top of the allocation.
   */
  _initBlastLights() {
    this.blastLights = new Pool(() => {
      const l = new THREE.PointLight(0xffc070, 0, 40, 2);
      // Deliberately NOT `visible = false`. Three.js excludes hidden lights
      // from the render list, so toggling visibility changes the light count
      // and forces a recompile of EVERY lit material in the scene — a
      // multi-second stall the first time a rocket detonated. Leaving them
      // permanently visible at zero intensity keeps the program stable; a
      // zero-intensity light costs a few ALU ops and nothing else.
      this.scene.add(l);
      return { light: l, t: -1, peak: 0, glow: 0 };
    }, 4);
  }

  _updateBlastLights(dt) {
    for (const b of this.blastLights.items) {
      if (b.t < 0) continue;
      b.t += dt;
      const spike = Math.max(0, 1 - b.t / 0.07) ** 2;
      const glow = Math.max(0, 1 - b.t / 0.85) ** 2.4;
      b.light.intensity = b.peak * spike + b.glow * glow;
      b.light.color.setRGB(1, 0.55 + spike * 0.34, 0.20 + spike * 0.52);
      if (b.t > 0.9) { b.t = -1; b.light.intensity = 0; }
    }
  }

  /**
   * Detonation. The whole thing is budgeted against a HDR pipeline whose bloom
   * threshold is 2.6 linear and whose tone map clips around 3.8: the flash core
   * is the ONLY element allowed past that, and it is a few dozen pixels wide.
   *
   * The old version put a 900 cd point light one metre above the deck, which
   * drove the asphalt underneath to ~10 linear — a white pool the size of the
   * lower third of the frame, with the fireball's own additive stack (16 puffs
   * summing past 30) sitting on top of it. Both are budgeted now.
   */
  explosion(pos, radius = 5) {
    const R = radius;
    const E = R * R;                      // radiated energy scales with area

    const b = this.blastLights.next();
    b.light.position.copy(pos).addScaledVector(UP, R * 0.45);
    b.light.distance = R * 7;
    // 12 cd/m² of blast energy: enough to throw hard shadows and colour the
    // whole street orange, not enough to clip the ground it stands on.
    b.peak = 12 * E;
    b.glow = 3.4 * E;
    b.t = 0;
    b.light.intensity = b.peak;

    _v3.copy(this.engine.camera.position).sub(pos).normalize();

    // Flash core: the one deliberately clipped element. Small, and gone in
    // three frames — a detonation flash is over before the eye resolves it.
    const fl = this.ring(pos, _v3, {
      color: 0xfff4e0, r0: R * 0.05, r1: R * 0.22, ttl: 0.055,
      thickness: 1.8, intensity: 3.2,
    });
    fl.mesh.renderOrder = 8;
    // Halo around it, well under the clip point, carrying the bloom.
    this.ring(pos, _v3, {
      color: 0xffb055, r0: R * 0.18, r1: R * 0.85, ttl: 0.13,
      thickness: 1.4, intensity: 0.55,
    });

    // Pressure wave: a thin camera-facing ring snapping outward.
    this.ring(pos, _v3, {
      color: 0xe8eef6, r0: R * 0.35, r1: R * 2.1, ttl: 0.26,
      thickness: 0.05, intensity: 0.26,
    });

    // Fireball, in two tiers. The heart is a handful of small, bright, short
    // puffs; the body is larger, cooler and lives long enough to roll over into
    // smoke. Gains are set so ~6 overlapping puffs peak near 3.5 linear.
    for (let i = 0; i < 7; i++) {
      _v.copy(pos).add(_v2.set(rnd() - 0.5, (rnd() - 0.5) * 0.7, rnd() - 0.5)
        .multiplyScalar(R * 0.30));
      const p = this.puff(_v, {
        fire: true, color: 0xffffff, gain: 0.62,
        size0: R * 0.16, size1: R * (0.34 + rnd() * 0.22),
        ttl: 0.20 + rnd() * 0.18, opacity: 0.75, drag: 3.4, rise: 1.4, turb: 4.0,
      });
      p.vel.set(rnd() - 0.5, rnd() * 0.8 + 0.2, rnd() - 0.5)
        .normalize().multiplyScalar(R * (1.6 + rnd() * 1.6));
    }
    for (let i = 0; i < 13; i++) {
      _v.copy(pos).add(_v2.set(rnd() - 0.5, (rnd() - 0.5) * 0.8, rnd() - 0.5)
        .multiplyScalar(R * 0.5));
      const p = this.puff(_v, {
        fire: true, color: 0xffffff, gain: 0.34,
        size0: R * 0.20, size1: R * (0.55 + rnd() * 0.45),
        ttl: 0.40 + rnd() * 0.45, opacity: 0.55, drag: 2.4, rise: 1.9, turb: 3.6,
      });
      p.vel.set(rnd() - 0.5, rnd() * 0.9 + 0.15, rnd() - 0.5)
        .normalize().multiplyScalar(R * (1.3 + rnd() * 1.7));
    }

    // Rolling smoke shell. Emitted a beat late so the fireball is briefly
    // unobstructed and the smoke reads as being *born* from it.
    const shell = (n, delay) => this._schedule(delay, () => {
      for (let i = 0; i < n; i++) {
        _v.copy(pos).add(_v2.set(rnd() - 0.5, (rnd() - 0.5) * 0.7, rnd() - 0.5)
          .multiplyScalar(R * 0.65));
        const p = this.puff(_v, {
          color: i % 3 === 0 ? 0x6d5f52 : 0x322f2b,
          size0: R * 0.28, size1: R * (0.95 + rnd() * 0.8),
          ttl: 1.9 + rnd() * 1.7, opacity: 0.58, drag: 1.5, rise: 1.2, turb: 2.6,
        });
        p.vel.set(rnd() - 0.5, rnd() * 0.7 + 0.25, rnd() - 0.5)
          .normalize().multiplyScalar(R * (0.7 + rnd()));
      }
    });
    shell(9, 0.02);
    shell(9, 0.12);

    // Ground shockwave: dust ring skimming the deck, plus a short heat ring.
    this.ring(_v.copy(pos).setY(pos.y - R * 0.08), UP, {
      color: 0xd8cbb4, r0: R * 0.2, r1: R * 2.4, ttl: 0.6,
      thickness: 0.20, intensity: 0.42,
    });
    this.ring(_v.copy(pos).setY(pos.y - R * 0.06), UP, {
      color: 0xffc98a, r0: R * 0.1, r1: R * 1.2, ttl: 0.20,
      thickness: 0.40, intensity: 0.55,
    });
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 + rnd();
      _v.set(pos.x + Math.cos(a) * R * 0.5, pos.y + 0.12, pos.z + Math.sin(a) * R * 0.5);
      const p = this.puff(_v, {
        color: 0x9d8f78, size0: R * 0.25, size1: R * (0.9 + rnd() * 0.6),
        ttl: 1.4 + rnd(), opacity: 0.4, drag: 1.8, rise: 0.4, turb: 2.0,
      });
      p.vel.set(Math.cos(a), 0.18, Math.sin(a)).multiplyScalar(R * 2.2);
    }

    // Debris cone + spark shower.
    this.spark(pos, UP, 60, 1.5, 2.6, {
      life: 0.9, size: 0.03, stretch: 0.022, temp: 1.2, gravity: 22, drag: 1.0, trail: 0.12,
    });
    this.chips(_v.copy(pos).setY(pos.y + 0.1), UP, 26, {
      color: 0x4a443c, size: 0.035, speed: 3.4, ttl: 2.6, spread: 0.85, lift: 3.5,
    });

    // Lingering smoke column that keeps feeding for a couple of seconds.
    for (let i = 1; i <= 5; i++) {
      this._schedule(i * 0.28, () => {
        for (let k = 0; k < 3; k++) {
          _v.copy(pos).add(_v2.set(rnd() - 0.5, rnd() * 0.5, rnd() - 0.5).multiplyScalar(R * 0.5));
          const p = this.puff(_v, {
            color: 0x3b3630, size0: R * 0.3, size1: R * (1.1 + rnd() * 0.7),
            ttl: 2.4 + rnd() * 2.0, opacity: 0.34, drag: 1.1, rise: 1.4, turb: 1.8,
          });
          p.vel.set((rnd() - 0.5) * R * 0.5, R * (0.5 + rnd() * 0.5), (rnd() - 0.5) * R * 0.5);
        }
      });
    }
  }

  /**
   * Wipe every transient back to nothing.
   *
   * A round restart used to leave the previous round's craters, scorch decals,
   * debris and smoke standing in the new one — the bomb site still looked
   * detonated before anyone had touched the bomb. Nothing here is destroyed;
   * every system is pooled, so clearing is just marking the pool dead and
   * pushing the buffers once.
   */
  clear() {
    for (const sys of [this.smoke, this.fire]) {
      if (!sys) continue;
      for (const p of sys.parts) { p.alive = false; p.life = 0; }
      sys.live = 0;
      // Zero the opacity column so nothing is drawn before the next update.
      sys.arrays.aLife.fill(0);
      sys.geo.attributes.aLife.needsUpdate = true;
    }
    for (const sys of [this.sparkSys, this.tracerSys, this.decalSys]) {
      if (!sys) continue;
      for (const p of sys.parts) { p.alive = false; p.life = 0; }
      // Decals carry their opacity in aParam.z, sparks and tracers in aLife.
      if (sys.arrays.aParam) {
        for (let i = 2; i < sys.arrays.aParam.length; i += 4) sys.arrays.aParam[i] = 0;
        sys.geo.attributes.aParam.needsUpdate = true;
      }
      if (sys.arrays.aLife) {
        sys.arrays.aLife.fill(0);
        sys.geo.attributes.aLife.needsUpdate = true;
      }
    }
    if (this.debrisSys) {
      for (const p of this.debrisSys.parts) { p.alive = false; p.life = 0; }
      // An InstancedMesh keeps drawing whatever matrices it holds, so the
      // instances have to be scaled to nothing rather than merely marked dead.
      this.debrisSys.dummy.scale.setScalar(0);
      this.debrisSys.dummy.position.set(0, -9999, 0);
      this.debrisSys.dummy.updateMatrix();
      for (let i = 0; i < this.debrisSys.mesh.count; i++) {
        this.debrisSys.mesh.setMatrixAt(i, this.debrisSys.dummy.matrix);
      }
      this.debrisSys.mesh.instanceMatrix.needsUpdate = true;
    }
    for (const s of this.shells?.items ?? []) {
      s.mesh.visible = false;
      if (s.body) { this.physics?.removeBody?.(s.body); s.body = null; }
    }
    for (const f of this.worldFlashes?.items ?? []) { f.mesh.visible = false; f.t = 0; f.ttl = 0; }
    for (const b of this.blastLights?.items ?? []) { b.t = -1; b.light.intensity = 0; }
    for (const l of this.extraLights?.items ?? []) { l.timer = 0; l.light.intensity = 0; }
    if (this.muzzleLight) this.muzzleLight.intensity = 0;
    if (this.vmLight) this.vmLight.intensity = 0;
    if (this.flashGroup) this.flashGroup.visible = false;
    this.flashTimer = 0;
    this.smokeAccum = 0;
  }

  /* ---- update --------------------------------------------------------- */
  update(dt) {
    this.time += dt;

    // Sun direction drives smoke lighting and decal shading.
    if (!this._sunLight) {
      this._sunLight = this.scene.children.find((o) => o.isDirectionalLight) || null;
    }
    if (this._sunLight) {
      this._sunWorld.copy(this._sunLight.position).sub(this._sunLight.target.position).normalize();
    }
    this._sunView.copy(this._sunWorld).transformDirection(this.engine.camera.matrixWorldInverse);

    // Muzzle flash decay.
    if (this.flashTimer > 0) {
      this.flashTimer -= dt;
      const k = Math.max(0, this.flashTimer / this.flashDur);
      this.flashGroup.scale.setScalar(0.82 + k * 0.28);
      this.flashCone.material.uniforms.uInten.value *= Math.exp(-14 * dt);
      if (this.flashTimer <= 0) this.flashGroup.visible = false;
    }
    // A 34/s decay is a 20 ms half-life — the light was gone before the frame
    // it was fired on had finished compositing. 22/s gives it ~31 ms, which is
    // still a flicker but one that lands on two or three frames.
    if (this.muzzleLight.intensity > 0) {
      this.muzzleLight.intensity *= Math.exp(-22 * dt);
      if (this.muzzleLight.intensity < 0.3) this.muzzleLight.intensity = 0;
    }
    if (this.vmLight.intensity > 0) {
      this.vmLight.intensity *= Math.exp(-24 * dt);
      if (this.vmLight.intensity < 0.02) this.vmLight.intensity = 0;
    }
    for (const e of this.extraLights.items) {
      if (e.timer > 0) {
        e.timer -= dt;
        e.light.intensity *= Math.exp(-26 * dt);
        if (e.timer <= 0) e.light.intensity = 0;
      }
    }
    this._updateWorldFlashes(dt);

    // Minimum screen-space thickness for tracers and sparks, in world units
    // per unit of view depth. Follows the FOV so ADS does not fatten them.
    const cam = this.engine.camera;
    const perPx = 2.0 * Math.tan(cam.fov * 0.5 * THREE.MathUtils.DEG2RAD)
      / Math.max(1, this.engine.size.height);
    this.tracerSys.mat.uniforms.uPx.value = perPx * 3.0;
    this.tracerSys.mat.uniforms.uPxMax.value = perPx * 7.0;
    this.sparkSys.mat.uniforms.uPx.value = perPx * 1.8;
    this.sparkSys.mat.uniforms.uPxMax.value = perPx * 6.0;

    this._updateTracers(dt);
    this._updateSparks(dt);
    this._updateSmokeSystem(this.smoke, dt);
    this._updateSmokeSystem(this.fire, dt);
    this._updateDecals(dt);
    this._updateDebris(dt);
    this._updateRings(dt);
    this._updateShells(dt);
    // This was written and then never called.
    //
    // `explosion()` sets the blast light to its peak — 12 x radius², which is
    // 768 for an RPG — and nothing ever brought it back down, so every
    // detonation left a permanent floodlight standing at the impact point.
    // Four of them, since that is the pool size, after which they recycle and
    // the glare simply moves. The decay is a quarter-second spike into a
    // 0.85 s afterglow; without it the spike never ended.
    this._updateBlastLights(dt);

    if (this._timers.length) {
      for (let i = this._timers.length - 1; i >= 0; i--) {
        const tm = this._timers[i];
        tm.t -= dt;
        if (tm.t <= 0) { this._timers.splice(i, 1); tm.fn(); }
      }
    }
    if (this._transients.length) {
      for (let i = this._transients.length - 1; i >= 0; i--) {
        if (!this._transients[i](dt)) this._transients.splice(i, 1);
      }
    }
  }
}
